import React, { useState } from 'react';
import { ParkingSpot } from '../types';
import { PlusCircle, Trash, ToggleLeft, ToggleRight, X, Sparkles, Navigation } from 'lucide-react';

interface ParkingProps {
  parkingSpots: ParkingSpot[];
  onAddParkingSpot: (spot: Omit<ParkingSpot, 'id'>) => void;
  onUpdateParkingSpot: (id: string, s: Partial<ParkingSpot>) => void;
  onDeleteParkingSpot: (id: string) => void;
  isAdmin: boolean;
}

export default function Parking({ parkingSpots, onAddParkingSpot, onUpdateParkingSpot, onDeleteParkingSpot, isAdmin }: ParkingProps) {
  const [number, setNumber] = useState('');
  const [error, setError] = useState('');

  const handleSubmit = (e: React.FormEvent) => {
    e.preventDefault();
    if (!number.trim()) {
      setError('Por favor, informe o número da vaga.');
      return;
    }

    // Check if duplicate number
    const duplicate = parkingSpots.some(p => p.number.trim() === number.trim());
    if (duplicate) {
      setError('Este número de vaga já está cadastrado.');
      return;
    }

    onAddParkingSpot({
      number: number.trim(),
      active: true,
      status: 'Disponível'
    });

    setNumber('');
    setError('');
  };

  const handleDelete = (spot: ParkingSpot) => {
    if (!isAdmin) {
      alert("Permissão negada. Apenas Administradores podem excluir permanentemente vagas do estacionamento.");
      return;
    }
    const hasActiveReservationOrAccommodation = spot.status === 'Ocupado';
    if (hasActiveReservationOrAccommodation) {
      if (!confirm(`A vaga ${spot.number} encontra-se registrada como ocupada. Deseja realmente excluí-la do sistema?`)) {
        return;
      }
    } else {
      if (!confirm(`Deseja realmente excluir permanentemente a vaga ${spot.number}?`)) {
        return;
      }
    }
    onDeleteParkingSpot(spot.id);
  };

  return (
    <div className="space-y-6">
      {/* Title block */}
      <div className="flex flex-col md:flex-row md:items-center justify-between gap-4 border-b border-zinc-200 pb-5">
        <div>
          <h1 id="header-title-parking" className="text-xl font-semibold tracking-tight text-zinc-950">
            Estacionamento Privativo
          </h1>
          <p className="text-xs text-zinc-500 mt-1">
            Gerencie as vagas de garagem disponíveis da pousada. Vagas ativas e disponíveis podem ser associadas às acomodações correspondentes.
          </p>
        </div>
      </div>

      <div className="grid grid-cols-1 lg:grid-cols-3 gap-6">
        {/* Left Side: Creation Form */}
        <div className="lg:col-span-1 bg-white border border-zinc-200/80 rounded-xl overflow-hidden shadow-sm h-fit">
          <div className="border-b border-zinc-100 bg-zinc-50 px-5 py-4">
            <h2 className="text-xs font-bold text-zinc-800 uppercase tracking-wider flex items-center gap-1.5">
              <PlusCircle className="w-4 h-4 text-zinc-500" /> Cadastrar Vaga de Estacionamento
            </h2>
          </div>
          <form onSubmit={handleSubmit} className="p-5 space-y-4">
            {error && (
              <div id="error-spot" className="p-2.5 bg-red-50 text-red-700 text-xxs font-semibold border border-red-100 rounded-lg">
                {error}
              </div>
            )}
            
            <div>
              <label htmlFor="input-spot-num" className="block text-xxs font-bold text-zinc-500 uppercase tracking-wider mb-1.5">
                Número da Vaga (Número)*
              </label>
              <input
                type="text"
                id="input-spot-num"
                value={number}
                onChange={(e) => {
                  setNumber(e.target.value);
                  setError('');
                }}
                className="block w-full text-xs py-2 px-3 border border-zinc-200 rounded-lg text-zinc-800 bg-zinc-50/30 focus:outline-none focus:ring-1 focus:ring-zinc-900 transition-colors"
                placeholder="Ex. 100, 101, A2..."
                required
              />
            </div>

            <div>
              <button
                type="submit"
                id="btn-submit-spot-add"
                className="w-full flex justify-center py-2 px-4 rounded-lg bg-green-700 hover:bg-green-600 text-white text-xs font-semibold uppercase tracking-wider transition-all cursor-pointer shadow-sm shadow-green-900/10 active:scale-[0.98]"
              >
                Cadastrar
              </button>
            </div>
          </form>
        </div>

        {/* Right Side: List & Table */}
        <div className="lg:col-span-2 bg-white border border-zinc-200/80 rounded-xl overflow-hidden shadow-sm">
          <div className="border-b border-zinc-100 bg-zinc-50 px-5 py-4">
            <h2 className="text-xs font-bold text-zinc-800 uppercase tracking-wider">
              Vagas Ativas no Sistema
            </h2>
          </div>

          {/* Desktop Table View */}
          <div className="hidden md:block overflow-x-auto">
            <table id="table-spots" className="w-full text-left text-xs border-collapse">
              <thead>
                <tr className="bg-zinc-50/50 border-b border-zinc-200 text-zinc-500 font-bold uppercase tracking-wider text-xxs">
                  <th className="py-3 px-4 w-12 text-center">#</th>
                  <th className="py-3 px-4">Número da Vaga</th>
                  <th className="py-3 px-4 text-center">Estado Operacional</th>
                  <th className="py-3 px-4 text-center">Situação (Vaga)</th>
                  <th className="py-3 px-4 text-right w-32">Ações</th>
                </tr>
              </thead>
              <tbody className="divide-y divide-zinc-200/60 font-medium text-zinc-700">
                {parkingSpots.length === 0 ? (
                  <tr>
                    <td colSpan={5} id="parking-empty-row" className="p-8 text-center text-zinc-400 font-normal">
                      Nenhuma vaga de estacionamento cadastrada.
                    </td>
                  </tr>
                ) : (
                  parkingSpots.map((spot, idx) => (
                    <tr key={spot.id} id={`spot-row-${spot.id}`} className="hover:bg-zinc-50/50 transition-colors">
                      <td className="py-3.5 px-4 font-mono text-zinc-400 text-xxs text-center">{idx + 1}</td>
                      <td className="py-3.5 px-4">
                        <div className="flex items-center gap-2">
                          <Navigation className="w-3.5 h-3.5 text-zinc-400 rotate-45" />
                          <span className="font-semibold text-zinc-900 text-xs">Vaga {spot.number}</span>
                        </div>
                      </td>
                      <td className="py-3.5 px-4 text-center">
                        <div className="flex justify-center">
                          {spot.active ? (
                            <span id={`spot-active-badge-${spot.id}`} className="inline-flex items-center px-2 py-0.5 rounded-full text-xxs font-bold uppercase tracking-wider bg-green-50 text-green-700 border border-green-200">
                              Ativo
                            </span>
                          ) : (
                            <span id={`spot-active-badge-${spot.id}`} className="inline-flex items-center px-2 py-0.5 rounded-full text-xxs font-bold uppercase tracking-wider bg-zinc-100 text-zinc-400 border border-zinc-200 font-normal">
                              Desativada
                            </span>
                          )}
                        </div>
                      </td>
                      <td className="py-3.5 px-4 text-center">
                        <div className="flex justify-center">
                          {spot.status === 'Ocupado' ? (
                            <span id={`spot-occupy-badge-${spot.id}`} className="inline-flex items-center px-2 py-0.5 rounded-md text-xxs font-semibold bg-amber-50 text-amber-800 border border-amber-200">
                              Ocupada
                            </span>
                          ) : (
                            <span id={`spot-occupy-badge-${spot.id}`} className="inline-flex items-center px-2 py-0.5 rounded-md text-xxs font-semibold bg-blue-50 text-blue-800 border border-blue-100">
                              Disponível
                            </span>
                          )}
                        </div>
                      </td>
                      <td className="py-3.5 px-4 text-right">
                        <div className="flex items-center justify-end gap-2">
                          <button
                            type="button"
                            id={`btn-toggle-spot-${spot.id}`}
                            title={spot.active ? "Desativar vaga" : "Ativar vaga"}
                            onClick={() => {
                              if (!isAdmin) {
                                alert("Apenas Administradores podem ativar/desativar vagas do estacionamento.");
                                return;
                              }
                              onUpdateParkingSpot(spot.id, { active: !spot.active });
                            }}
                            className={`p-1 rounded border transition-colors cursor-pointer ${
                              spot.active 
                                ? 'text-green-600 hover:text-green-800 border-green-200 bg-green-50/20 hover:bg-green-50/40' 
                                : 'text-zinc-400 hover:text-zinc-600 border-zinc-200 bg-zinc-50 hover:bg-zinc-100'
                            }`}
                          >
                            {spot.active ? <ToggleRight className="w-4 h-4" /> : <ToggleLeft className="w-4 h-4" />}
                          </button>
                          
                          <button
                            type="button"
                            id={`btn-delete-spot-${spot.id}`}
                            title="Excluir vaga de estacionamento"
                            onClick={() => handleDelete(spot)}
                            className="p-1 text-red-600 hover:text-red-800 border border-red-200 hover:border-red-300 rounded cursor-pointer transition-colors bg-red-50/20"
                          >
                            <X className="w-3.5 h-3.5" />
                          </button>
                        </div>
                      </td>
                    </tr>
                  ))
                )}
              </tbody>
            </table>
          </div>

          {/* Mobile Card List View */}
          <div className="md:hidden divide-y divide-zinc-200">
            {parkingSpots.length === 0 ? (
              <div id="parking-empty-mobile" className="p-8 text-center text-zinc-400 font-normal text-xs bg-white">
                Nenhuma vaga de estacionamento cadastrada.
              </div>
            ) : (
              parkingSpots.map((spot, idx) => (
                <div key={spot.id} id={`spot-card-mobile-${spot.id}`} className="p-4 bg-white space-y-3">
                  <div className="flex items-center justify-between">
                    <div className="flex items-center gap-2">
                      <Navigation className="w-3.5 h-3.5 text-zinc-500 rotate-45" />
                      <span className="font-semibold text-zinc-900 text-sm">Vaga {spot.number}</span>
                    </div>
                    <span className="text-[10px] text-zinc-400 font-mono">Vaga #{idx + 1}</span>
                  </div>

                  <div className="flex items-center gap-3">
                    <div>
                      {spot.active ? (
                        <span className="inline-flex items-center px-2 py-0.5 rounded-full text-[10px] font-bold uppercase bg-green-50 text-green-700 border border-green-200">
                          Ativa
                        </span>
                      ) : (
                        <span className="inline-flex items-center px-2 py-0.5 rounded-full text-[10px] font-normal uppercase bg-zinc-100 text-zinc-400 border border-zinc-200">
                          Desativada
                        </span>
                      )}
                    </div>
                    <div>
                      {spot.status === 'Ocupado' ? (
                        <span className="inline-flex items-center px-2 py-0.5 rounded-md text-[10px] font-semibold bg-amber-50 text-amber-800 border border-amber-200">
                          Ocupada
                        </span>
                      ) : (
                        <span className="inline-flex items-center px-2 py-0.5 rounded-md text-[10px] font-semibold bg-blue-50 text-blue-800 border border-blue-100">
                          Disponível
                        </span>
                      )}
                    </div>
                  </div>

                  <div className="flex items-center justify-end gap-2 border-t border-zinc-100 pt-2.5">
                    <button
                      type="button"
                      id={`btn-mobile-toggle-spot-${spot.id}`}
                      onClick={() => {
                        if (!isAdmin) {
                          alert("Apenas Administradores podem ativar/desativar vagas do estacionamento.");
                          return;
                        }
                        onUpdateParkingSpot(spot.id, { active: !spot.active });
                      }}
                      className={`flex items-center gap-1.5 px-3 py-1.5 rounded-lg border text-[11px] font-bold uppercase transition-colors cursor-pointer ${
                        spot.active
                          ? 'text-green-700 hover:text-green-800 border-green-200 bg-green-50'
                          : 'text-zinc-500 hover:text-zinc-700 border-zinc-200 bg-zinc-50'
                      }`}
                    >
                      {spot.active ? <ToggleRight className="w-3.5 h-3.5" /> : <ToggleLeft className="w-3.5 h-3.5" />}
                      <span>{spot.active ? 'Ativa' : 'Ativar'}</span>
                    </button>
                    
                    <button
                      type="button"
                      id={`btn-mobile-delete-spot-${spot.id}`}
                      onClick={() => handleDelete(spot)}
                      className="flex items-center gap-1.5 px-3 py-1.5 text-red-600 hover:text-red-700 border border-red-200 rounded-lg text-[11px] font-bold uppercase cursor-pointer transition-colors bg-red-50"
                    >
                      <X className="w-3.5 h-3.5" />
                      <span>Excluir</span>
                    </button>
                  </div>
                </div>
              ))
            )}
          </div>

        </div>
      </div>
    </div>
  );
}
